import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { History, CheckCircle, XCircle, Clock, RefreshCw, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useFilters } from '../hooks/useFilters';
import { usePagination } from '../hooks/usePagination';
import { formatDateTime } from '../lib/utils';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const UploadHistory = () => {
  const { user } = useAuth();
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({
    totalUploads: 0,
    successfulUploads: 0,
    failedUploads: 0
  });
  
  // Use custom hooks for filtering and pagination
  const { filters, filteredItems, updateFilter, resetFilters, hasActiveFilters } = useFilters(uploads, {
    searchFields: ['filename', 'dataType'],
    dateField: 'uploadedAt'
  });
  
  const {
    currentPage,
    totalPages,
    currentItems: paginatedUploads,
    nextPage,
    prevPage,
    goToPage,
    resetPage,
    hasNextPage,
    hasPrevPage
  } = usePagination(filteredItems, 10);

  const dataTypes = [
    { value: '', label: 'All Types' },
    { value: 'sales', label: 'Sales Data' },
    { value: 'inventory', label: 'Inventory Data' },
    { value: 'returns', label: 'Returns Data' },
    { value: 'kpi', label: 'Store KPI Data' },
    { value: 'product_master', label: 'Product Master' },
    { value: 'store_master', label: 'Store Master' },
    { value: 'vendor_master', label: 'Vendor Master' },
    { value: 'promotion', label: 'Promotion Performance' }
  ];

  const fetchSummary = async () => {
    try {
      // Fetch KPIs with larger date range (365 days) to capture all uploads
      const response = await axios.get(
        `${BACKEND_URL}/api/upload-jobs/kpis`,
        {
          params: { days: 365 },
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      const kpis = response.data.kpis;
      setSummary({
        totalUploads: kpis.totalUploads,
        successful: kpis.successfulUploads,
        failed: kpis.failedUploads,
        pending: kpis.pendingUploads,
        processing: kpis.processingUploads
      });
    } catch (err) {
      console.error('Error fetching upload summary:', err);
      // Fallback: Calculate from history if API fails
    }
  };

  const fetchHistory = async () => {
    setLoading(true);
    try {
      // Fetch from new upload-jobs API
      const response = await axios.get(
        `${BACKEND_URL}/api/upload-jobs`,
        { 
          params: { limit: 500 },
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      // Map jobs to upload history format
      const jobs = response.data.jobs || [];
      const mappedUploads = jobs.map(job => ({
        jobId: job.jobId,
        filename: job.filename,
        dataType: job.entity,
        numRows: job.insertedRows || job.totalRows || 0,
        totalRows: job.totalRows,
        insertedRows: job.insertedRows,
        failedRows: job.failedRows,
        status: job.status.toLowerCase(),
        uploadedAt: job.startedAt,
        uploadedAtIST: job.startedAtIST, // Server-side IST conversion
        finishedAt: job.finishedAt,
        finishedAtIST: job.finishedAtIST, // Server-side IST conversion
        uploadMode: job.uploadMode,
        verificationStatus: job.verificationStatus,
        error: job.error
      }));

      setUploads(mappedUploads);
      resetPage();
    } catch (err) {
      console.error('Error fetching upload history:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
    fetchSummary();
  }, []);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-600" />;
      case 'processing':
        return <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />;
      case 'pending':
        return <Clock className="h-5 w-5 text-yellow-600" />;
      default:
        return <Clock className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      success: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
      failed: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
      pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
    };
    return badges[status] || badges.pending;
  };

  // V28: Use global formatDateTime from utils.js for canonical IST display

  const exportToCSV = () => {
    const headers = ['Filename', 'Rows', 'Status', 'Uploaded At'];
    // Use filteredItems to export only the filtered data
    const rows = filteredItems.map(u => [
      u.filename,
      u.numRows,
      u.status,
      formatDateTime(u.uploadedAt)
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `upload_history_${new Date().toISOString()}.csv`;
    link.click();
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="upload-history-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <History className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Upload History</h1>
            <p className="text-slate-600 dark:text-slate-400">Last 20-25 file uploads</p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <Button
            onClick={fetchHistory}
            variant="outline"
            size="sm"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          
          <Button
            onClick={exportToCSV}
            variant="outline"
            size="sm"
            disabled={filteredItems.length === 0}
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Summary Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-700 dark:text-blue-300">Total Uploads</p>
                <p className="text-3xl font-bold text-blue-900 dark:text-blue-100 mt-2">{summary.totalUploads}</p>
              </div>
              <div className="h-12 w-12 bg-blue-200 dark:bg-blue-800 rounded-full flex items-center justify-center">
                <History className="h-6 w-6 text-blue-700 dark:text-blue-300" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border-green-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-700 dark:text-green-300">Successful Uploads</p>
                <p className="text-3xl font-bold text-green-900 dark:text-green-100 mt-2">{summary.successful || 0}</p>
              </div>
              <div className="h-12 w-12 bg-green-200 dark:bg-green-800 rounded-full flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-green-700 dark:text-green-300" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 border-red-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-red-700 dark:text-red-300">Failed Uploads</p>
                <p className="text-3xl font-bold text-red-900 dark:text-red-100 mt-2">{summary.failed || 0}</p>
              </div>
              <div className="h-12 w-12 bg-red-200 dark:bg-red-800 rounded-full flex items-center justify-center">
                <XCircle className="h-6 w-6 text-red-700 dark:text-red-300" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter and Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Search Filename
              </label>
              <input
                type="text"
                value={filters.search}
                onChange={(e) => {
                  updateFilter('search', e.target.value);
                  resetPage();
                }}
                placeholder="Search by filename..."
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white"
              />
            </div>
            
            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Filter by Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => {
                  updateFilter('status', e.target.value);
                  resetPage();
                }}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white"
              >
                <option value="all">All Status</option>
                <option value="success">Success</option>
                <option value="failed">Failed</option>
                <option value="pending">Pending</option>
              </select>
            </div>
            
            {/* Clear Filters */}
            <div className="flex items-end">
              <Button
                onClick={() => {
                  resetFilters();
                  resetPage();
                }}
                variant="outline"
                className="w-full"
                disabled={!hasActiveFilters}
              >
                Clear Filters
              </Button>
            </div>
          </div>
          
          {/* Results Count */}
          {uploads.length > 0 && (
            <div className="mt-4 text-sm text-slate-600 dark:text-slate-400">
              Showing {paginatedUploads.length} of {filteredItems.length} uploads
              {filteredItems.length !== uploads.length && ` (filtered from ${uploads.length} total)`}
            </div>
          )}
        </CardContent>
      </Card>

      {/* History Table */}
      <Card>
        <CardHeader>
          <CardTitle>Upload Records</CardTitle>
          <CardDescription>Recent file uploads and their processing status</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <History className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>
                {uploads.length === 0 
                  ? 'No uploads found'
                  : 'No uploads match the current filters'}
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-slate-700">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                        Status
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                        Filename
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                        Rows
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                        Uploaded At
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedUploads.map((upload, index) => (
                      <tr 
                        key={index}
                        className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                      >
                        <td className="py-4 px-4">
                          <div className="flex items-center space-x-2">
                            {getStatusIcon(upload.status)}
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(upload.status)}`}>
                              {upload.status}
                            </span>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <span className="text-sm text-slate-900 dark:text-slate-100 font-medium">
                            {upload.filename}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <span className="text-sm text-slate-600 dark:text-slate-400">
                            {upload.numRows.toLocaleString()}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <span className="text-sm text-slate-600 dark:text-slate-400">
                            {formatDateTime(upload.uploadedAt)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
                  <div className="text-sm text-slate-600 dark:text-slate-400">
                    Page {currentPage} of {totalPages}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={prevPage}
                      disabled={!hasPrevPage}
                      variant="outline"
                      size="sm"
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Previous
                    </Button>
                    
                    {[...Array(Math.min(5, totalPages))].map((_, i) => {
                      const pageNum = i + 1;
                      return (
                        <Button
                          key={i}
                          onClick={() => goToPage(pageNum)}
                          variant={currentPage === pageNum ? 'default' : 'outline'}
                          size="sm"
                          className={currentPage === pageNum ? 'bg-blue-600 text-white' : ''}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                    
                    <Button
                      onClick={nextPage}
                      disabled={!hasNextPage}
                      variant="outline"
                      size="sm"
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default UploadHistory;
